/**
 * useProjectPersistence Hook
 * Extracted from MultiProjectContext.tsx
 * Handles localStorage persistence of project selections and preferences
 */

import { useEffect } from 'react';
import type { ProjectInfo } from '@/features/projects/services/projectService';
import { log } from '@/shared/utils/logger';

interface LoadedState {
  selections: string[];
  hasPreferences: boolean;
  activeProjectId: string | null;
}

const PROJECT_STATE_STORAGE_KEY = 'unified_project_state';

export type ProjectPersistenceStorageScope = 'local' | 'session';

interface ProjectPersistenceOptions {
  userId?: string | null;
  storageScope?: ProjectPersistenceStorageScope;
}

const getProjectStateStorage = (storageScope: ProjectPersistenceStorageScope = 'local'): Storage => (
  storageScope === 'session' ? sessionStorage : localStorage
);

const getProjectStateStorageKey = ({
  userId,
}: ProjectPersistenceOptions = {}): string => {
  if (userId) {
    return `${PROJECT_STATE_STORAGE_KEY}:${userId}`;
  }
  return PROJECT_STATE_STORAGE_KEY;
};

const readStoredProjectState = (options: ProjectPersistenceOptions = {}): Record<string, unknown> | null => {
  try {
    const storage = getProjectStateStorage(options.storageScope);
    const storageKey = getProjectStateStorageKey(options);
    const storedState = storage.getItem(storageKey);
    return storedState ? JSON.parse(storedState) : null;
  } catch (e) {
    log.error('MultiProject', 'Error reading stored project state:', e);
    return null;
  }
};

const isValidProjectInfo = (value: unknown): value is ProjectInfo => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.projectId === 'string' &&
    candidate.projectId.trim().length > 0 &&
    typeof candidate.projectName === 'string' &&
    candidate.projectName.trim().length > 0
  );
};

export function loadSavedProjectCatalog(options: ProjectPersistenceOptions = {}): ProjectInfo[] {
  const appState = readStoredProjectState(options);
  const storedProjects = Array.isArray(appState?.availableProjects) ? appState.availableProjects : [];

  return storedProjects
    .filter(isValidProjectInfo)
    .map(project => ({ ...project }));
}

export function persistProjectCatalog(projects: ProjectInfo[], options: ProjectPersistenceOptions = {}): void {
  try {
    const appState = readStoredProjectState(options) ?? {};
    const cleanedProjects = projects
      .filter(isValidProjectInfo)
      .map(project => ({ ...project }));

    getProjectStateStorage(options.storageScope).setItem(
      getProjectStateStorageKey(options),
      JSON.stringify({
        ...appState,
        availableProjects: cleanedProjects,
      })
    );
  } catch (e) {
    log.error('MultiProject', 'Error saving project catalog:', e);
  }
}

export function loadSavedProjectStateUserId(options: ProjectPersistenceOptions = {}): string | null {
  const appState = readStoredProjectState(options);
  const userId = appState?.projectStateUserId;
  return typeof userId === 'string' && userId.trim().length > 0 ? userId : null;
}

export function migrateLegacyProjectStateForUser(options: ProjectPersistenceOptions): void {
  if (!options.userId) return;
  try {
    const storage = getProjectStateStorage(options.storageScope);
    const storageKey = getProjectStateStorageKey(options);
    if (storage.getItem(storageKey)) return;
    const legacyState = readStoredProjectState({ storageScope: options.storageScope });
    if (legacyState?.projectStateUserId !== options.userId) return;
    storage.setItem(storageKey, JSON.stringify(legacyState));
  } catch (error) {
    // Preference migration is best-effort. Preserve the legacy value and allow
    // the authenticated workspace to load its authoritative server data.
    log.error('MultiProject', 'Error migrating stored project state:', error);
  }
}

/**
 * Load saved project selections from localStorage
 */
export function loadSavedSelections(options: ProjectPersistenceOptions = {}): LoadedState {
  try {
    const appState = readStoredProjectState(options);
    if (appState) {
      // If user has preferences, return their exact selections (even if empty)
      if (appState.hasUserPreferences) {
        const storedSelections: unknown = appState.selectedProjectIds;
        let selections: string[];
        const savedActiveProjectId = typeof appState.activeProjectId === 'string' && appState.activeProjectId.trim().length > 0
          ? appState.activeProjectId
          : null;

        // Ensure selections is an array (defensive coding)
        if (!Array.isArray(storedSelections)) {
          log.error('MultiProject', 'Invalid selectedProjectIds in storage (not an array):', storedSelections);
          // If it's a string with commas, try to split it
          if (typeof storedSelections === 'string' && storedSelections.includes(',')) {
            selections = storedSelections.split(',').map(s => s.trim());
          } else if (typeof storedSelections === 'string') {
            selections = [storedSelections];
          } else {
            selections = [];
          }
        } else {
          selections = storedSelections.filter((id): id is string => typeof id === 'string');
        }

        // Clean up any individual IDs that contain commas (shouldn't happen but defensive)
        selections = selections.map((id: string) => {
          if (typeof id === 'string' && id.includes(',')) {
            log.warn('MultiProject', 'Cleaning up comma-separated project ID in saved state:', id);
            return id.split(',')[0].trim();
          }
          return id;
        }).filter((id: string) => id && id !== 'undefined' && id !== 'null' && id !== '');

        // Validate that we have at least some valid selections if claiming preferences
        // If all selections were invalid, reset preferences flag
        if (selections.length === 0 && Array.isArray(storedSelections) && storedSelections.length > 0) {
          log.warn('MultiProject', 'All saved selections were invalid, resetting preferences');
          return {
            selections: [],
            hasPreferences: false,
            activeProjectId: null
          };
        }

        // Loading saved project selections
        return {
          selections,
          hasPreferences: true,
          activeProjectId: savedActiveProjectId && selections.includes(savedActiveProjectId)
            ? savedActiveProjectId
            : (selections[0] || null)
        };
      }
    }
  } catch (e) {
    log.error('MultiProject', 'Error loading saved selections:', e);
  }
  // No saved preferences - return empty with flag indicating no preferences
  return { selections: [], hasPreferences: false, activeProjectId: null };
}

interface UseProjectPersistenceParams {
  userId?: string | null;
  storageScope?: ProjectPersistenceStorageScope;
  availableProjects: ProjectInfo[];
  selectedProjectIds: string[];
  activeProjectId: string | null;
  hasInitialized: boolean;
  isOwnerStorageReady: boolean;
}

/**
 * Hook to persist project selections to localStorage
 */
export function useProjectPersistence({
  userId,
  storageScope = 'local',
  availableProjects,
  selectedProjectIds,
  activeProjectId,
  hasInitialized,
  isOwnerStorageReady,
}: UseProjectPersistenceParams) {

  // The catalog can change from a realtime metadata frame without a reload.
  // Persist only after MultiProjectContext has rehydrated the active owner's
  // storage key; otherwise a user/view-as transition could write the previous
  // owner's in-memory catalog into the next owner's storage namespace.
  useEffect(() => {
    if (!hasInitialized || !isOwnerStorageReady) return;
    persistProjectCatalog(availableProjects, { userId, storageScope });
  }, [userId, storageScope, availableProjects, hasInitialized, isOwnerStorageReady]);

  // Save project selections to localStorage whenever they change
  useEffect(() => {
    // Skip saving during initial load before initialization
    if (!hasInitialized || !isOwnerStorageReady) return;

    try {
      const options = { userId, storageScope };
      const appState = readStoredProjectState(options) ?? {};

      // Check if this is a user-initiated change (not the initial load)
      const isUserChange = hasInitialized && (
        appState.hasUserPreferences ||
        (appState.selectedProjectIds &&
         JSON.stringify(appState.selectedProjectIds) !== JSON.stringify(selectedProjectIds))
      );

      // Save selected project IDs and mark that user has preferences if changed
      const updatedState = {
        ...appState,
        selectedProjectIds,
        activeProjectId,
        projectStateUserId: userId || appState.projectStateUserId || null,
        hasUserPreferences: appState.hasUserPreferences || isUserChange
      };

      getProjectStateStorage(storageScope).setItem(getProjectStateStorageKey(options), JSON.stringify(updatedState));

      if (isUserChange) {
        log.info('MultiProject', 'Saved user project selections:', selectedProjectIds);
      }
    } catch (e) {
      log.error('MultiProject', 'Error saving project selections:', e);
    }
  }, [userId, storageScope, selectedProjectIds, activeProjectId, hasInitialized, isOwnerStorageReady]);
}
